import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiSave, FiX, FiUser, FiBriefcase, FiAlertCircle, FiDollarSign, FiChevronLeft, FiChevronRight, FiPackage } from 'react-icons/fi';
import { useVendedor } from '../../hooks/useVendedores';
import ConfirmModal from '../../components/UI/Modal/ConfirmModal';
import { productosService } from '../../services';

const VendedorForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    vendedor,
    isEditing,
    isLoading,
    error,
    hasChanges,
    updateVendedorData,
    saveVendedor,
    resetVendedor,
    clearError
  } = useVendedor(id);

  const [validationErrors, setValidationErrors] = useState({});
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoadingZipCode, setIsLoadingZipCode] = useState(false);

  // Estados para el carousel de productos
  const [productos, setProductos] = useState([]);
  const [currentProductIndex, setCurrentProductIndex] = useState(0);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);

  // Opciones para los selectores
  const frecuenciasPago = [
    { value: 'WEEKLY', label: 'SEMANAL' },
    { value: 'BIWEEKLY', label: 'QUINCENAL' },
    { value: 'MONTHLY', label: 'MENSUAL' }
  ];

  const metodosPago = [
    { value: 'CHECK', label: 'CHEQUE' },
    { value: 'BANK_TRANSFER', label: 'DEPÓSITO' },
    { value: 'ZELLE', label: 'ZELLE' }
  ];

  const paises = [
    { value: 'Estados Unidos', label: 'Estados Unidos' },
    { value: 'Canadá', label: 'Canadá' },
    { value: 'Puerto Rico', label: 'Puerto Rico' }
  ];

  const estados = [
    { value: 'AL', label: 'Alabama' },
    { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' },
    { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' },
    { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' },
    { value: 'DE', label: 'Delaware' },
    { value: 'FL', label: 'Florida' },
    { value: 'GA', label: 'Georgia' },
    { value: 'HI', label: 'Hawaii' },
    { value: 'ID', label: 'Idaho' },
    { value: 'IL', label: 'Illinois' },
    { value: 'IN', label: 'Indiana' },
    { value: 'IA', label: 'Iowa' },
    { value: 'KS', label: 'Kansas' },
    { value: 'KY', label: 'Kentucky' },
    { value: 'LA', label: 'Louisiana' },
    { value: 'ME', label: 'Maine' },
    { value: 'MD', label: 'Maryland' },
    { value: 'MA', label: 'Massachusetts' },
    { value: 'MI', label: 'Michigan' },
    { value: 'MN', label: 'Minnesota' },
    { value: 'MS', label: 'Mississippi' },
    { value: 'MO', label: 'Missouri' },
    { value: 'MT', label: 'Montana' },
    { value: 'NE', label: 'Nebraska' },
    { value: 'NV', label: 'Nevada' },
    { value: 'NH', label: 'New Hampshire' },
    { value: 'NJ', label: 'New Jersey' },
    { value: 'NM', label: 'New Mexico' },
    { value: 'NY', label: 'New York' },
    { value: 'NC', label: 'North Carolina' },
    { value: 'ND', label: 'North Dakota' },
    { value: 'OH', label: 'Ohio' },
    { value: 'OK', label: 'Oklahoma' },
    { value: 'OR', label: 'Oregon' },
    { value: 'PA', label: 'Pennsylvania' },
    { value: 'RI', label: 'Rhode Island' },
    { value: 'SC', label: 'South Carolina' },
    { value: 'SD', label: 'South Dakota' },
    { value: 'TN', label: 'Tennessee' },
    { value: 'TX', label: 'Texas' },
    { value: 'UT', label: 'Utah' },
    { value: 'VT', label: 'Vermont' },
    { value: 'VA', label: 'Virginia' },
    { value: 'WA', label: 'Washington' },
    { value: 'WV', label: 'West Virginia' },
    { value: 'WI', label: 'Wisconsin' },
    { value: 'WY', label: 'Wyoming' }
  ];

  const idiomas = [
    { value: 'es', label: 'Español' },
    { value: 'en', label: 'English' }
  ];

  const timezones = [
    { value: 'America/Bogota', label: 'Bogotá (GMT-5)' },
    { value: 'America/New_York', label: 'New York (GMT-5)' },
    { value: 'America/Los_Angeles', label: 'Los Angeles (GMT-8)' },
    { value: 'America/Mexico_City', label: 'Ciudad de México (GMT-6)' }
  ];

  // Limpiar errores cuando se monta el componente
  useEffect(() => {
    clearError();
    setValidationErrors({});
  }, [clearError]);

  // Cargar productos aleatorios para el carousel
  useEffect(() => {
    loadProductos();
  }, []);

  const loadProductos = async () => {
    setIsLoadingProducts(true);
    try {
      // SOLO productos marcados explícitamente como "nuevos" (checkbox en el
      // producto). Se priorizan los que tienen foto para el carrusel.
      const response = await productosService.getProductos({
        status: 'ACTIVE',
        is_new: true,
        limit: 12
      });

      if (response.success && response.data) {
        const lista = response.data;
        const conFoto = lista.filter(p => Array.isArray(p.images) && p.images.length > 0);
        const sinFoto = lista.filter(p => !Array.isArray(p.images) || p.images.length === 0);
        setProductos([...conFoto, ...sinFoto]);
      }
    } catch (error) {
      console.error('Error al cargar productos:', error);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const handlePrevProduct = () => {
    setCurrentProductIndex((prev) =>
      prev === 0 ? productos.length - 1 : prev - 1
    );
  };

  const handleNextProduct = () => {
    setCurrentProductIndex((prev) =>
      prev === productos.length - 1 ? 0 : prev + 1
    );
  };

  const handleInputChange = (field, value) => {
    updateVendedorData(field, value);

    // Lógica especial para comisión
    if (field === 'commissionPercentage') {
      // Asegurar que esté entre 0 y 100
      const numValue = parseFloat(value);
      if (numValue > 100) {
        updateVendedorData(field, 100);
      } else if (numValue < 0) {
        updateVendedorData(field, 0);
      }
    }

    // Limpiar error de validación del campo específico
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Función para buscar información por código postal
  const handleZipCodeLookup = async (zipCode) => {
    if (!zipCode || zipCode.length < 5) return;

    setIsLoadingZipCode(true);

    try {
      // API gratuita de Zippopotam para USA
      const response = await fetch(`https://api.zippopotam.us/us/${zipCode}`);

      if (response.ok) {
        const data = await response.json();

        if (data.places && data.places.length > 0) {
          const place = data.places[0];

          // Autocompletar campos de dirección
          updateVendedorData('city', place['place name']);
          updateVendedorData('state', place['state abbreviation']);
          updateVendedorData('country', 'Estados Unidos');
        }
      }
    } catch (error) {
      console.error('Error al buscar código postal:', error);
    } finally {
      setIsLoadingZipCode(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setIsSaving(true);
    setValidationErrors({});
    
    try {
      const result = await saveVendedor();

      if (result.success) {
        // Navegar de vuelta a la lista con mensaje de éxito
        navigate('/system/vendedores', {
          state: {
            message: result.message,
            type: 'success'
          }
        });
      } else {
        // Procesar errores de validación
        const processedErrors = {};

        // Si hay validation_errors (array format)
        if (result.validation_errors && Array.isArray(result.validation_errors)) {
          result.validation_errors.forEach(err => {
            processedErrors[err.field] = err.message;
          });
        }

        // Si hay validationErrors (object format)
        if (result.validationErrors && typeof result.validationErrors === 'object') {
          Object.assign(processedErrors, result.validationErrors);
        }

        // Si no hay errores específicos pero hay error general
        if (Object.keys(processedErrors).length === 0 && result.error) {
          const errorMsg = result.error;
          if (errorMsg.toLowerCase().includes('email already registered') || errorMsg.toLowerCase().includes('email already')) {
            processedErrors.email = 'Este email ya está registrado. Use un email diferente.';
          } else if (errorMsg.toLowerCase().includes('failed to create user')) {
            processedErrors.general = 'Error al crear el vendedor. Verifique los datos e intente nuevamente.';
          } else {
            processedErrors.general = errorMsg;
          }
        }

        setValidationErrors(processedErrors);
      }
    } catch (err) {
      setValidationErrors({ 
        general: 'Error inesperado al guardar el vendedor' 
      });
      console.error('Unexpected error:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      setShowUnsavedChangesModal(true);
    } else {
      navigate('/system/vendedores');
    }
  };

  const confirmCancel = () => {
    resetVendedor();
    navigate('/system/vendedores');
  };

  if (isLoading) {
    return (
      <div className="page-loading">
        <div className="loading-spinner-single" style={{ width: '40px', height: '40px' }}></div>
        <div className="loading-message">
          {isEditing ? 'Cargando vendedor...' : 'Preparando formulario...'}
        </div>
      </div>
    );
  }

  return (
    <div className="cliente-form-page">
      {/* Header */}
      <div className="form-header">
        <button 
          className="btn btn--ghost"
          onClick={handleCancel}
          type="button"
        >
          <FiArrowLeft /> Volver a Vendedores
        </button>
        
        <h1 className="form-title">
          {isEditing ? 'EDITAR VENDEDOR' : 'NUEVO VENDEDOR'}
        </h1>
        
        <div className="form-actions">
          <button 
            className="btn btn--outline"
            onClick={handleCancel}
            type="button"
            disabled={isSaving}
          >
            <FiX /> Cancelar
          </button>
          
          <button 
            className="btn btn--primary"
            onClick={handleSubmit}
            disabled={isSaving || !hasChanges}
            type="submit"
          >
            {isSaving ? (
              <>
                <div className="loading-spinner-single" style={{ width: '16px', height: '16px', marginRight: '8px' }}></div>
                Guardando...
              </>
            ) : (
              <>
                <FiSave /> {isEditing ? 'Actualizar' : 'Crear'} Vendedor
              </>
            )}
          </button>
        </div>
      </div>

      {/* Errores generales */}
      {(error || validationErrors.general) && (
        <div className="alert alert--danger">
          <FiAlertCircle className="alert-icon" />
          <div className="alert-content">
            <strong>Error:</strong> {error || validationErrors.general}
          </div>
        </div>
      )}

      {/* Errores de validación específicos */}
      {Object.keys(validationErrors).length > 1 || (Object.keys(validationErrors).length === 1 && !validationErrors.general) ? (
        <div className="alert alert--warning">
          <FiAlertCircle className="alert-icon" />
          <div className="alert-content">
            <strong>Errores de validación:</strong>
            <ul className="error-list">
              {Object.entries(validationErrors)
                .filter(([field]) => field !== 'general')
                .map(([field, message]) => (
                  <li key={field}>{message}</li>
                ))
              }
            </ul>
          </div>
        </div>
      ) : null}

      {/* Formulario */}
      <form onSubmit={handleSubmit} className="cliente-form">
        <div className="form-columns">
          {/* Columna Izquierda */}
          <div className="form-column">
            {/* Sección: Datos Personales */}
            <div className="form-section">
              <div className="section-header">
                <FiUser className="section-icon" />
                <h2 className="section-title">Datos Personales</h2>
              </div>

              <div className="form-group">
                <label className="form-label required">Nombre</label>
                <input
                  type="text"
                  className={`form-input ${validationErrors.fullName ? 'error' : ''}`}
                  value={vendedor.fullName || ''}
                  onChange={(e) => handleInputChange('fullName', e.target.value)}
                  placeholder="Ingrese el nombre completo"
                  maxLength={255}
                />
                {validationErrors.fullName && (
                  <span className="form-error">{validationErrors.fullName}</span>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Teléfonos</label>
                {(vendedor.phones && vendedor.phones.length > 0) ? (
                  <>
                    {vendedor.phones.map((phone, index) => (
                      <div key={index} className="phone-input-group" style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                        <select
                          className="form-input"
                          value={phone.type || 'mobile'}
                          onChange={(e) => {
                            const newPhones = [...vendedor.phones];
                            newPhones[index].type = e.target.value;
                            handleInputChange('phones', newPhones);
                          }}
                          style={{ width: '140px', height: '42px', padding: '10px 12px' }}
                        >
                          <option value="mobile">Móvil</option>
                          <option value="office">Oficina</option>
                          <option value="home">Casa</option>
                          <option value="fax">Fax</option>
                          <option value="other">Otro</option>
                        </select>
                        <input
                          type="tel"
                          className="form-input"
                          value={phone.number || ''}
                          onChange={(e) => {
                            const newPhones = [...vendedor.phones];
                            newPhones[index].number = e.target.value;
                            handleInputChange('phones', newPhones);
                          }}
                          placeholder="(000) 000-0000"
                          style={{ flex: 1 }}
                        />
                        <button
                          type="button"
                          className="btn btn--ghost"
                          onClick={() => {
                            const newPhones = vendedor.phones.filter((_, i) => i !== index);
                            // Si eliminamos el principal, hacer principal al primero
                            if (phone.is_primary && newPhones.length > 0) {
                              newPhones[0].is_primary = true;
                            }
                            handleInputChange('phones', newPhones.length > 0 ? newPhones : null);
                          }}
                          style={{ padding: '8px 12px', minWidth: 'auto' }}
                          title="Eliminar teléfono"
                        >
                          <FiX />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="btn btn--outline btn--sm"
                      onClick={() => {
                        const newPhones = vendedor.phones || [];
                        newPhones.push({
                          number: '',
                          type: 'mobile',
                          is_primary: newPhones.length === 0
                        });
                        handleInputChange('phones', newPhones);
                      }}
                      style={{ marginTop: '8px' }}
                    >
                      + Agregar Teléfono
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="btn btn--outline"
                    onClick={() => {
                      handleInputChange('phones', [{
                        number: '',
                        type: 'mobile',
                        is_primary: true
                      }]);
                    }}
                  >
                    + Agregar Teléfono
                  </button>
                )}
                {validationErrors.phones && (
                  <span className="form-error">{validationErrors.phones}</span>
                )}
              </div>

              <div className="form-group">
                <label className="form-label required">Email</label>
                <input
                  type="email"
                  className={`form-input ${validationErrors.email ? 'error' : ''}`}
                  value={vendedor.email || ''}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="correo@ejemplo.com"
                />
                {validationErrors.email && (
                  <span className="form-error">{validationErrors.email}</span>
                )}
              </div>

              {!isEditing && (
                <div className="form-group">
                  <label className="form-label required">Contraseña</label>
                  <div className="password-input-container">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className={`form-input ${validationErrors.password ? 'error' : ''}`}
                      value={vendedor.password || ''}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      placeholder="Contraseña (mínimo 8 caracteres)"
                      minLength={8}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                  {validationErrors.password && (
                    <span className="form-error">{validationErrors.password}</span>
                  )}
                </div>
              )}
            </div>

            {/* Sección: Dirección */}
            <div className="form-section">
              <div className="section-header">
                <h3 className="section-title">Dirección</h3>
              </div>

              <div className="form-group">
                <label className="form-label">Número y Calle</label>
                <input
                  type="text"
                  className="form-input"
                  value={vendedor.street || ''}
                  onChange={(e) => handleInputChange('street', e.target.value)}
                  placeholder="NÚMERO, CALLE"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Unidad</label>
                <input
                  type="text"
                  className="form-input"
                  value={vendedor.unit || ''}
                  onChange={(e) => handleInputChange('unit', e.target.value)}
                  placeholder="UNIDAD"
                />
              </div>

              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">Ciudad</label>
                  <input
                    type="text"
                    className="form-input"
                    value={vendedor.city || ''}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    placeholder="CIUDAD"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Estado</label>
                  <input
                    type="text"
                    className="form-input"
                    value={vendedor.state || ''}
                    onChange={(e) => handleInputChange('state', e.target.value)}
                    placeholder="ESTADO"
                  />
                </div>
              </div>

              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">Código Postal</label>
                  <input
                    type="text"
                    className="form-input"
                    value={vendedor.postalCode || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleInputChange('postalCode', value);
                      if (value && value.length === 5) {
                        handleZipCodeLookup(value);
                      }
                    }}
                    placeholder="CÓDIGO POSTAL"
                    maxLength="5"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">País</label>
                  <select
                    className="form-input"
                    value={vendedor.country || 'Estados Unidos'}
                    onChange={(e) => handleInputChange('country', e.target.value)}
                  >
                    {paises.map(pais => (
                      <option key={pais.value} value={pais.value}>{pais.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Columna Derecha */}
          <div className="form-column">
            {/* Sección: Información Bancaria */}
            <div className="form-section">
              <div className="section-header">
                <h3 className="section-title">Información de Cuenta Bancaria</h3>
              </div>

              <div className="form-group">
                <label className="form-label">Número de Cuenta</label>
                <input
                  type="text"
                  className="form-input"
                  value={vendedor.bankAccount || ''}
                  onChange={(e) => handleInputChange('bankAccount', e.target.value)}
                  placeholder="NÚMERO DE CUENTA"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Banco Receptor</label>
                <input
                  type="text"
                  className="form-input"
                  value={vendedor.bankName || ''}
                  onChange={(e) => handleInputChange('bankName', e.target.value)}
                  placeholder="BANCO RECEPTOR"
                />
              </div>
            </div>

            {/* Sección: Comisiones y Pagos */}
            <div className="form-section">
              <div className="section-header">
                <FiDollarSign className="section-icon" />
                <h3 className="section-title">Comisiones y Pagos</h3>
              </div>

              <div className="form-group">
                <label className="form-label">% de Comisión</label>
                <input
                  type="number"
                  className="form-input"
                  value={vendedor.commissionPercentage || ''}
                  onChange={(e) => handleInputChange('commissionPercentage', parseFloat(e.target.value) || 0)}
                  // La rueda del mouse cambiaba el valor sin querer al hacer
                  // scroll con el campo enfocado (10 → 9.94 = 6 ticks de 0.01)
                  onWheel={(e) => e.target.blur()}
                  placeholder="0"
                  min="0"
                  max="100"
                  step="1"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Frecuencia de Pago</label>
                <select
                  className="form-input"
                  value={vendedor.paymentPeriod || 'MONTHLY'}
                  onChange={(e) => handleInputChange('paymentPeriod', e.target.value)}
                >
                  {frecuenciasPago.map(freq => (
                    <option key={freq.value} value={freq.value}>{freq.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Forma de Pago</label>
                <select
                  className="form-input"
                  value={vendedor.paymentMethod || 'BANK_TRANSFER'}
                  onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
                >
                  {metodosPago.map(metodo => (
                    <option key={metodo.value} value={metodo.value}>{metodo.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Sección: Carousel de Productos */}
            <div className="form-section">
              <div className="section-header">
                <FiPackage className="section-icon" />
                <h3 className="section-title">Productos Nuevos</h3>
              </div>

              {isLoadingProducts ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                  <div className="loading-spinner-single" style={{ width: '30px', height: '30px' }}></div>
                </div>
              ) : productos.length > 0 ? (
                <div style={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  {/* Flecha Izquierda */}
                  <button
                    type="button"
                    onClick={handlePrevProduct}
                    style={{
                      background: '#4a3470',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      width: '40px',
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      flexShrink: 0,
                      transition: 'all 0.2s',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = '#3d2b5c';
                      e.currentTarget.style.transform = 'scale(1.1)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = '#4a3470';
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  >
                    <FiChevronLeft size={22} />
                  </button>

                  {/* Contenedor de 3 productos */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: '12px',
                    flex: 1,
                    maxWidth: '100%'
                  }}>
                    {[0, 1, 2, 3].map((offset) => {
                      const index = (currentProductIndex + offset) % productos.length;
                      const producto = productos[index];

                      // Obtener la imagen principal o la primera imagen disponible
                      const getProductImage = (product) => {
                        if (!product.images || product.images.length === 0) return null;

                        // Buscar imagen principal
                        const primaryImage = product.images.find(img => img.is_primary);
                        if (primaryImage) return primaryImage;

                        // Si no hay principal, usar la primera
                        return product.images[0];
                      };

                      const imagen = getProductImage(producto);
                      const API_BASE_URL = process.env.REACT_APP_API_URL?.replace('/api/v1', '') || 'http://localhost:8000';
                      const imageUrl = imagen ? `${API_BASE_URL}${imagen.medium_url}` : null;

                      return (
                        <div
                          key={index}
                          onClick={() => navigate(`/system/productos/${producto.id}`)}
                          style={{
                            width: '100%',
                            paddingBottom: '100%',
                            position: 'relative',
                            background: '#f8f9fa',
                            borderRadius: '10px',
                            overflow: 'hidden',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            transition: 'transform 0.2s, box-shadow 0.2s, border-color 0.2s',
                            cursor: 'pointer',
                            border: '2px solid #e9ecef'
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.transform = 'scale(1.05)';
                            e.currentTarget.style.boxShadow = '0 4px 16px rgba(74, 52, 112, 0.2)';
                            e.currentTarget.style.borderColor = '#4a3470';
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                            e.currentTarget.style.borderColor = '#e9ecef';
                          }}
                        >
                          {imageUrl ? (
                            <img
                              src={imageUrl}
                              alt={imagen.alt_text || 'Producto'}
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                const parent = e.target.parentElement;
                                const iconDiv = document.createElement('div');
                                iconDiv.style.cssText = 'position: absolute; top: 0; left: 0; width: 100%; height: 100%; display: flex; align-items: center; justify-content: center;';
                                iconDiv.innerHTML = '<svg stroke="currentColor" fill="none" stroke-width="2" viewBox="0 0 24 24" stroke-linecap="round" stroke-linejoin="round" style="width: 50%; height: 50%; color: #6c757d;" xmlns="http://www.w3.org/2000/svg"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>';
                                parent.appendChild(iconDiv);
                              }}
                            />
                          ) : (
                            <div style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <FiPackage style={{ fontSize: '64px', color: '#6c757d' }} />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Flecha Derecha */}
                  <button
                    type="button"
                    onClick={handleNextProduct}
                    style={{
                      background: '#4a3470',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      width: '40px',
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      flexShrink: 0,
                      transition: 'all 0.2s',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = '#3d2b5c';
                      e.currentTarget.style.transform = 'scale(1.1)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = '#4a3470';
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  >
                    <FiChevronRight size={22} />
                  </button>
                </div>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '20px',
                  color: '#6c757d',
                  fontSize: '14px'
                }}>
                  No hay productos disponibles
                </div>
              )}
            </div>
          </div>
        </div>
      </form>
      {/* Acciones inferiores: mismos manejadores que el encabezado */}
      <div className="form-actions bottom-form-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', padding: '24px 0', flexWrap: 'wrap' }}>
        
        <button type="button" className="btn btn--primary" onClick={handleSubmit} disabled={isSaving || !hasChanges}>
          <FiSave /> {isEditing ? 'Actualizar' : 'Crear'} Vendedor
        </button>
      </div>


      {/* Modal de confirmación para cambios no guardados */}
      <ConfirmModal
        isOpen={showUnsavedChangesModal}
        onClose={() => setShowUnsavedChangesModal(false)}
        onConfirm={confirmCancel}
        title="¿Descartar cambios?"
        message="Tienes cambios sin guardar. ¿Estás seguro de que quieres salir sin guardar?"
        confirmText="Sí, descartar"
        cancelText="Continuar editando"
        type="warning"
      />
    </div>
  );
};

export default VendedorForm;